import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import DimensionGrid from './components/DimensionGrid';
import ScenarioIndices from './components/ScenarioIndices';
import RadarChart from './components/RadarChart';
import EventFeed from './components/EventFeed';
import TimeSeriesChart from './components/TimeSeriesChart';
import ChartGuide from './components/ChartGuide';
import AlertBanner from './components/AlertBanner';
import WhitePaper from './components/WhitePaper';
import Disclaimer from './components/Disclaimer';
import CookieConsent from './components/CookieConsent';
import PixelIcon from './components/PixelIcon';

// Use relative URL in production (proxied by nginx), absolute URL in development
// Ensure API URL always ends with /api unless it's already included
const getApiUrl = () => {
  const base = process.env.REACT_APP_API_URL;
  if (!base) return '/api';
  if (base.endsWith('/api')) return base;
  if (base.endsWith('/')) return `${base}api`;
  return `${base}/api`;
};

const API_URL = getApiUrl();

function App() {
  const [dimensions, setDimensions] = useState(null);
  const [indices, setIndices] = useState(null);
  const [events, setEvents] = useState([]);
  const [historical, setHistorical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  // ai27.org/#api opens the Methodology panel at the Public API section
  const [whitePaperSection, setWhitePaperSection] = useState(() =>
    typeof window !== 'undefined' && window.location.hash === '#api' ? 'api' : null
  );
  const [showWhitePaper, setShowWhitePaper] = useState(() =>
    typeof window !== 'undefined' && window.location.hash === '#api'
  );
  const openWhitePaper = (section = null) => {
    setWhitePaperSection(section);
    setShowWhitePaper(true);
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [dimRes, indRes, eventRes, histRes] = await Promise.all([
        fetch(`${API_URL}/dimensions`),
        fetch(`${API_URL}/indices`),
        fetch(`${API_URL}/events`),
        fetch(`${API_URL}/historical`)
      ]);

      if (!dimRes.ok || !indRes.ok || !eventRes.ok || !histRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const [dimData, indData, eventData, histData] = await Promise.all([
        dimRes.json(),
        indRes.json(),
        eventRes.json(),
        histRes.json()
      ]);

      setDimensions(dimData);
      setIndices(indData);
      setEvents(eventData);
      setHistorical(histData);
      setLastUpdated(new Date());
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to connect to monitoring service');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-box pixel-border">
            <div className="loading-text glow-text">
              INITIALIZING AI MONITORING SYSTEM
              <span className="loading-dots">...</span>
            </div>
            <div className="loading-bar">
              <div className="loading-bar-fill"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Header lastUpdated={lastUpdated} />
        <div className="error-screen">
          <div className="error-box pixel-border">
            <div className="error-icon"><PixelIcon name="warning" size={40} /></div>
            <div className="error-text text-red">{error}</div>
            <button className="pixel-button mt-2" onClick={fetchData}>
              RETRY CONNECTION
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="scan-line"></div>
      <Header lastUpdated={lastUpdated} onOpenWhitePaper={() => openWhitePaper()} />
      
      {showWhitePaper && <WhitePaper section={whitePaperSection} onClose={() => setShowWhitePaper(false)} />}
      
      <main className="main-content" id="main">
        <div className="content-wrapper">
          {indices && <AlertBanner indices={indices} />}

          {/* Scenario Indices */}
          {indices && <ScenarioIndices indices={indices} />}
          
          {/* Main Visualization Grid */}
          <div className="viz-grid">
            {/* Radar Chart */}
            {dimensions && (
              <div className="viz-section">
                <RadarChart dimensions={dimensions} />
              </div>
            )}
            
            {/* Time Series */}
            {historical && historical.length > 0 && (
              <div className="viz-section">
                <TimeSeriesChart data={historical} />
              </div>
            )}
          </div>
          
          {historical && historical.length > 0 && <ChartGuide dimensions={dimensions} />}
          
          {/* Dimension Grid */}
          {dimensions && <DimensionGrid dimensions={dimensions} />}
          
          {/* Event Feed */}
          {events && events.length > 0 && <EventFeed events={events} />}
          
          {/* Disclaimer */}
          <Disclaimer />
          
        </div>
      </main>
      
      <footer className="footer">
        <div className="footer-content">
          <span className="status-dot active"></span>
          <span>SYSTEM OPERATIONAL</span>
          <span className="footer-divider">|</span>
          <span className="text-secondary">MONITORING ACTIVE</span>
          <span className="footer-divider">|</span>
          <span className="text-cyan">AI-mageddon v1.1 · Index v2.0</span>
          <span className="footer-divider">|</span>
          <a href="#api" className="footer-link" onClick={(e) => { e.preventDefault(); openWhitePaper('api'); }}>
            Public API · CC BY 4.0
          </a>
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
}

export default App;
